import { useCreateChatroomMutation } from '@pba/react-chat-core';
import { useCallback, useMemo } from 'react';
import { message } from 'ui';
import { useUser } from 'features/auth';
import { useSendChatMessage } from '../../hooks';
import type { ChatCreateFormValues } from '../ChatCreate';

type P = {
  onChatroomCreated?: (chatroomId: string) => void;
  onCreated: (chatroomId: string) => Promise<void>;
};

export const useCreateChat = ({ onChatroomCreated, onCreated }: P) => {
  const { nodeUserData } = useUser();
  const { mutate: createChatroom } = useCreateChatroomMutation();
  const { sendChatMessage } = useSendChatMessage();

  const createChat = useCallback(
    ({ attachments, members = [], message: messageText }: ChatCreateFormValues) => {
      const currentUserId = nodeUserData._id;
      const otherMembers = members.filter((member) => member.value !== currentUserId);

      const participants: Record<string, { email: string; firstName: string; lastName: string }> = {
        [currentUserId]: {
          email: nodeUserData.email,
          firstName: nodeUserData.firstName ?? '',
          lastName: nodeUserData.lastName ?? '',
        },
      };

      for (const member of otherMembers) {
        participants[member.value] = {
          email: member.email,
          firstName: member.firstName,
          lastName: member.lastName,
        };
      }

      const isGroupChat = otherMembers.length > 1;
      const memberIds = otherMembers.map((member) => member.value);

      createChatroom(
        {
          admins: isGroupChat ? [currentUserId] : [currentUserId, ...memberIds],
          participants,
          tags: isGroupChat ? ['group'] : ['direct'],
          type: 'practice',
          users: memberIds,
        },
        {
          onError() {
            message.error("Something went wrong. Chat couldn't be created.");
          },
          onSuccess(chatroom) {
            const isNewChatroom = !chatroom.message && !chatroom.stats;

            onChatroomCreated?.(chatroom._id);

            sendChatMessage({
              attachments,
              chatroomId: chatroom._id,
              messageText,
              async onError() {
                // TODO: find a way to keep the message in the chatroom if it fails to send
                await onCreated(chatroom._id);
              },
              async onSuccess() {
                message.success(isNewChatroom ? 'Chat created successfully' : 'Redirected to existing chat');
                await onCreated(chatroom._id);
              },
            });
          },
        },
      );
    },
    [createChatroom, nodeUserData, onChatroomCreated, onCreated, sendChatMessage],
  );

  return useMemo(() => ({ createChat }), [createChat]);
};
